import React, { InputHTMLAttributes, ReactNode, useState } from "react";
import {
  formatStringToNumber,
  IntlNubmerFormatter,
} from "../../utility/formatInputField";
import { isBlank } from "../kos/Kos";

/**
 * required input for the Input Component
 */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label: string;
  id: string;
  className?: string;
  labelClassName?: string;
  labelopts: {
    labelOn?: boolean;
    defaultLabelStyle?: boolean;
  };
  children?: ReactNode;
  flex?: boolean;
  checkbox?: boolean;
  disabled?: boolean;
  isNumberField?: boolean;
  defaultChecked?: boolean;
}

//predefined styling
const styles = `outline-none relative justify-content
  py-2 pl-3 text-lg border-2 border-sk-primary-blue font-medium rounded-md
  text-black hover:border-blue-500 focus:ring-2 focus:ring-blue-600`;

/**
 * @see App.css if you need arrows
 * creates an optional Label and Input field component
 * @param name, name of input field
 * @param label, label text
 * @param labelOpts, whether label tag is rendered and whether default style is enabled.
 * @param id, the given id of the input component.
 * @param className, custom css properties.
 * @param children, rest of htmlElements
 * @param rest, rest of htmlInputFieldProps.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    labelOn,
    defaultLabelStyle,
  }: { labelOn?: boolean; defaultLabelStyle?: boolean } = props.labelopts;
  const { labelClassName, className, label, name } = props;
  const checkbox = props.checkbox;
  const isNumberField = props.isNumberField;
  const [value, setValue] = useState(props.value as string);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value, name } = e.target;
    if (isBlank(e.target.value)) setValue(value);
    if (formatStringToNumber(value) || value === "0") {
      const toNumber = formatStringToNumber(value);
      const num = IntlNubmerFormatter(toNumber);
      setValue(num);
    }

    if (props?.onChange !== undefined) {
      const event = {
        ...e,
        target: {
          ...e.target,
          value: value.replace(/\D/g, ""),
          name: name,
        },
      };
      console.log(event);
      props.onChange(event);
    }
  };

  let checkboxContent = (
    <div {...(props?.flex && "className='flex'")}>
      <label className="inline-flex items-center" htmlFor={props.id}>
        <input
          {...props}
          className={"form-checkbox h-8 w-8"}
          ref={ref}
          type={props.type}
          inputMode={props.inputMode}
          defaultChecked={props.defaultChecked ?? false}
        />
        <span className="text-xl text-black mt-2 mb-2 ml-3 flex">{label}</span>
      </label>
    </div>
  );
  let inputContent = (
    <div className={`${props.flex ? "flex" : ""}`}>
      {labelOn ? (
        <label
          className={`${
            defaultLabelStyle
              ? "text-xl text-black mt-2 mb-2 flex"
              : labelClassName
          } `}
          htmlFor={props.id}
        >
          {label}
        </label>
      ) : null}
      <input
        {...props}
        className={`${styles} ${className}`}
        ref={ref}
        type={props.type}
        inputMode={props.inputMode}
        disabled={props.disabled}
        onFocus={(e) => e.target.select()}
        onWheel={(e) => e.currentTarget.blur()}
      />
    </div>
  );
  let numberInputContent = (
    <div className={`${props.flex ? "flex" : ""}`}>
      {labelOn ? (
        <label
          className={`${
            defaultLabelStyle
              ? "text-xl text-black mt-2 mb-2 flex"
              : labelClassName
          } `}
          htmlFor={props.id}
        >
          {label}
        </label>
      ) : null}
      <input
        {...props}
        className={`${styles} ${className}`}
        ref={ref}
        onChange={handleChange}
        value={value}
        type={props.type}
        inputMode={props.inputMode}
        disabled={props.disabled}
        onFocus={(e) => e.target.select()}
        onWheel={(e) => e.currentTarget.blur()}
      />
    </div>
  );
  if (isNumberField) {
    inputContent = numberInputContent;
  }

  if (checkbox) {
    inputContent = checkboxContent;
  }
  return inputContent;
});

export default Input;
